/**
 * Take-off, flight time and landing of a tracked aircraft (phase 2b.4 T10; Diurnal parity 03-034; decision 64:
 * clients see them too; docs/specs/tracker.md §5.3). Worked out from the aircraft's trail each time it changes, so
 * it is the same in every map window and after a restart (the trail is kept 48 hours, decision 75).
 *
 * The Diurnal rules (`updateFlightStatus`, am:2235–2316; `formatFlightStatusLabel`, am:2324–2359): airborne from
 * 40 knots (fixed-wing) or 20 knots (helicopter); the first speed under the threshold is the landing, but a return
 * above it within 2 minutes is a speed dip and keeps the take-off; an aircraft first seen already airborne has an
 * approximate take-off ("~T/O"); a tracker's own "Take Off" event a little before the speed crosses the threshold
 * is the take-off (the Diurnal tool refined it from V2Track's Take Off event, am:7149–7196; Spider Tracks names one
 * too); the status is shown for 12 hours after the landing (or take-off). Flight time shows from 0.05 h.
 */
import type { AircraftKind, FlightStatus } from './aircraft-layer.js';
import { formatOffset, utcToLocalMs } from './time-model.js';
import { AIRBORNE_KT, flyingFromTrail, type LandingStrip, type TrackPoint } from './tracker.js';

/** The flight status is shown for 12 hours (am:2190). */
export const FLIGHT_STATUS_HOURS = 12;
/** A return above the threshold within 2 minutes is a speed dip, not a new flight (am:2296–2314). */
export const SPEED_DIP_MS = 2 * 60_000;
/** A tracker's take-off event counts when the speed crosses the threshold within 10 minutes of it. */
export const TAKEOFF_EVENT_WINDOW_MS = 10 * 60_000;
/** After a tracker's landing word, a fast fix this far above the landing is a new flight, not the landing roll. */
const CLIMBED_AFTER_LANDING_FT = 500;

const TAKEOFF_EVENT = /\btake\s*-?\s*off\b/i;
const LANDING_EVENT = /\blanding\b|\blanded\b/i;

/**
 * The aircraft's last flight from its trail (any order; points without a speed are skipped), or null when the trail
 * shows none in the last 12 hours, or for a ground vehicle. A tracker's "Landing" word while airborne is the landing
 * (TracPlus and V2Track send it at 42 to 45 kt, above a fixed-wing's 40 kt; the "Enroute" or report of the landing
 * roll after it does not fly again unless a "Take Off" word or a 500 ft climb says so), and a trail whose last fix is
 * judged on the ground by the landing evidence (`flyingFromTrail`: slow after a descent at a known aerodrome, at
 * the flight's own start-up position or at a strip of the project's flight plan, `strips`; decision 949) has
 * landed at that fix, so the flight line and the aircraft's state agree.
 */
export function flightStatus(
  trail: readonly TrackPoint[],
  kind: AircraftKind,
  nowMs: number,
  strips: readonly LandingStrip[] = [],
): FlightStatus | null {
  const threshold = AIRBORNE_KT[kind];
  if (!Number.isFinite(threshold)) return null;
  const pts = trail
    .filter((p) => p.speedKt !== null && Number.isFinite(p.speedKt) && Number.isFinite(p.timeMs))
    .slice()
    .sort((a, b) => a.timeMs - b.timeMs);
  let state: 'unknown' | 'ground' | 'airborne' | 'landed' = 'unknown';
  let takeoff = 0;
  let approximate = false;
  let landing = 0;
  /** The altitude at a landing word (null: the landing came from the speed). */
  let landingWordAlt: number | null | undefined;
  let takeoffEvent: number | null = null;
  for (const p of pts) {
    const fast = (p.speedKt as number) >= threshold;
    const event = p.event ?? '';
    const landingWord = LANDING_EVENT.test(event);
    if (state === 'unknown') {
      if (fast && !landingWord) {
        state = 'airborne';
        takeoff = p.timeMs;
        approximate = true;
      } else state = 'ground';
    } else if (state === 'ground') {
      if (fast && !landingWord) {
        state = 'airborne';
        takeoff =
          takeoffEvent !== null && p.timeMs - takeoffEvent <= TAKEOFF_EVENT_WINDOW_MS
            ? takeoffEvent
            : p.timeMs;
        approximate = false;
      }
    } else if (state === 'airborne') {
      if (!fast || landingWord) {
        state = 'landed';
        landing = p.timeMs;
        landingWordAlt = landingWord ? p.altFt : undefined;
      }
    } else if (fast && !landingWord) {
      // after a landing word, only a take-off word or a climb flies again (the roll's report is not a flight)
      const rolled =
        landingWordAlt !== undefined &&
        !TAKEOFF_EVENT.test(event) &&
        !(
          landingWordAlt !== null &&
          p.altFt !== null &&
          p.altFt >= landingWordAlt + CLIMBED_AFTER_LANDING_FT
        );
      if (!rolled) {
        // landed: a short dip keeps the take-off; a longer stop is a new flight
        if (p.timeMs - landing > SPEED_DIP_MS) {
          takeoff =
            takeoffEvent !== null &&
            takeoffEvent > landing &&
            p.timeMs - takeoffEvent <= TAKEOFF_EVENT_WINDOW_MS
              ? takeoffEvent
              : p.timeMs;
          approximate = false;
        }
        state = 'airborne';
        landing = 0;
        landingWordAlt = undefined;
      }
    }
    if (TAKEOFF_EVENT.test(event) && state !== 'airborne') takeoffEvent = p.timeMs;
    // a tracker's landing event inside the dip window is the landing time
    if (landingWord && state === 'landed' && p.timeMs - landing <= SPEED_DIP_MS) {
      landing = p.timeMs;
      landingWordAlt = p.altFt;
    }
  }
  // airborne at the end, but the last fix is a landing for all the trail says: landed there
  const last = pts[pts.length - 1];
  if (state === 'airborne' && last && flyingFromTrail(trail, last.timeMs, kind, strips) === false) {
    state = 'landed';
    landing = last.timeMs;
  }
  if (state !== 'airborne' && state !== 'landed') return null;
  const ref = state === 'landed' ? landing : takeoff;
  if (nowMs - ref > FLIGHT_STATUS_HOURS * 3_600_000) return null;
  const end = state === 'landed' ? landing : Math.max(nowMs, takeoff);
  return {
    takeoffMs: takeoff,
    approximate,
    landingMs: state === 'landed' ? landing : null,
    hours: (end - takeoff) / 3_600_000,
  };
}

const hhmm = (utcMs: number, offsetH: number): string => {
  const d = new Date(utcToLocalMs(utcMs, offsetH));
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

/** Flight time shows from 0.05 h (am:2324–2359). */
export const FLIGHT_TIME_SHOWN_H = 0.05;

/**
 * The flight time of the flight line, "FT 1.4h" (hours to one decimal), or null under 0.05 h. The phone's Flights
 * tab uses the same words for the time so far of a flight it holds (decision 986, `phoneFlightTimeSoFarLabel`).
 */
export function flightTimeLabel(hours: number): string | null {
  return hours >= FLIGHT_TIME_SHOWN_H ? `FT ${hours.toFixed(1)}h` : null;
}

/**
 * The label beside the aircraft and in the aircraft list, in the project's time zone (the Diurnal tool used the
 * laptop's): "T/O 08:12 · FT 1.4h · LDG 09:36", "~T/O 08:12 · FT 0.6h" when the take-off was not seen.
 */
export function flightStatusLabel(f: FlightStatus, offsetH: number): string {
  const parts = [`${f.approximate ? '~' : ''}T/O ${hhmm(f.takeoffMs, offsetH)}`];
  const ft = flightTimeLabel(f.hours);
  if (ft !== null) parts.push(ft);
  if (f.landingMs !== null) parts.push(`LDG ${hhmm(f.landingMs, offsetH)}`);
  return parts.join(' · ');
}

/** The same in words for a screen reader and the tooltip, with the zone: "Take-off 08:12 (UTC+8), …". */
export function flightStatusWords(f: FlightStatus, offsetH: number): string {
  const zone = `UTC${formatOffset(offsetH)}`;
  const parts = [
    `${f.approximate ? 'Take-off about' : 'Take-off'} ${hhmm(f.takeoffMs, offsetH)} (${zone})`,
    `flight time ${f.hours.toFixed(1)} h`,
  ];
  if (f.landingMs !== null) parts.push(`landing ${hhmm(f.landingMs, offsetH)}`);
  return parts.join(', ');
}
